import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Field, Input } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { UNRESOLVED_FACTS } from '../../lib/company';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthShell, fieldErrors } from './AuthShell';

function LegalLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
    >
      {children}
    </Link>
  );
}

export function Register() {
  usePageTitle('Create your account — WaveQA');

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepted, setAccepted] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await api.register({
        email,
        password,
        confirm_password: confirm,
        accept_terms: accepted,
        display_name: displayName || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('Something went wrong.', 0));
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="If that address can be registered, a confirmation link is on its way. The link expires in 24 hours and can only be used once."
      >
        <Alert tone="info">
          The message is deliberately vague about whether an account already
          existed — that keeps this page from being usable to discover who has
          one. If you already have an account, you will have been emailed about
          that instead.
        </Alert>

        <p className="text-sm text-hull-300">
          Nothing arrived?{' '}
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="text-surge-400 underline hover:text-surge-300"
          >
            Try a different address
          </button>
          .
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Free plan: one agent, 20 hours every month."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-surge-400 hover:text-surge-300">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && <Alert tone="error">{error.message}</Alert>}

        <Field label="Email" htmlFor="email" errors={fieldErrors(error?.fields, 'email')}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={Boolean(fieldErrors(error?.fields, 'email'))}
          />
        </Field>

        <Field label="Name" htmlFor="name" hint="Optional">
          <Input
            id="name"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          hint="At least 12 characters, with upper and lower case, a digit and a symbol."
          errors={fieldErrors(error?.fields, 'password')}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            invalid={Boolean(fieldErrors(error?.fields, 'password'))}
          />
        </Field>

        <Field
          label="Confirm password"
          htmlFor="confirm"
          errors={fieldErrors(error?.fields, 'confirm_password')}
        >
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            invalid={Boolean(fieldErrors(error?.fields, 'confirm_password'))}
          />
        </Field>

        <label className="mb-5 flex items-start gap-2.5 text-sm text-hull-300">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            required
            className="mt-0.5 h-4 w-4 rounded border-hull-600 bg-hull-950"
          />
          {/* The two links open in a new tab: a customer who taps "terms" from
              a half-filled signup form should not lose what they have typed.
              Clicking a link inside a label does not toggle the checkbox —
              interactive descendants suppress the label's activation. */}
          <span>
            I accept the <LegalLink to="/terms">Terms of Service</LegalLink> and{' '}
            <LegalLink to="/privacy">Privacy Policy</LegalLink>.
            {UNRESOLVED_FACTS.length > 0 && (
              <span className="mt-0.5 block text-xs text-hull-500">
                Commercial and legal wording is pending review before launch.
              </span>
            )}
          </span>
        </label>

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  );
}
